import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import Pagination from '../../../../components/FlowBuilder/Common/Pagination/index.tsx';
import { EllipsisWithTooltip } from '../../../../components/Shared/Tables/TableV1.tsx';
import useOptinUsers from '../../hooks/useOptinUsers';
import useTemplateSx from '../../hooks/useTemplateSx';
import TemplateDataTable from '../shared/TemplateDataTable';
import OptoutUserModal from './OptoutUserModal';

const FIVE_HOURS_THIRTY_MINS_MS = (5 * 60 + 30) * 60 * 1000;

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    let raw = dateStr;
    if (typeof raw === 'string') {
      raw = raw.trim();
    }
    const parseable =
      typeof raw === 'string' && raw.includes(' ') && !raw.includes('T')
        ? raw.replace(' ', 'T')
        : raw;
    const dateObj = new Date(parseable);
    const validDate = !Number.isNaN(dateObj.getTime()) ? dateObj : new Date(raw);
    if (Number.isNaN(validDate.getTime())) {
      return dateStr;
    }
    const adjusted = new Date(validDate.getTime() + FIVE_HOURS_THIRTY_MINS_MS);
    return adjusted.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function OptedInChip({ value }) {
  return (
    <Chip
      label={value ? 'Opted In' : 'Opted Out'}
      color={value ? 'success' : 'error'}
      size="small"
    />
  );
}

export default function OptinUsersPanel({ onTotalCountChange }) {
  const styles = useTemplateSx();
  const {
    rows,
    totalCount,
    page,
    rowsPerPage,
    isLoading,
    isDownloading,
    isOptingOut,
    handleChangePage,
    handleChangeRowsPerPage,
    refresh,
    downloadList,
    optoutUser,
  } = useOptinUsers();

  const [optoutModalOpen, setOptoutModalOpen] = useState(false);
  const [optoutTargetPhone, setOptoutTargetPhone] = useState('');

  useEffect(() => {
    onTotalCountChange?.(totalCount);
  }, [onTotalCountChange, totalCount]);

  const columns = useMemo(
    () => [
      {
        header: '#',
        accessorKey: '_index',
        enableSorting: false,
        size: 55,
        Cell: ({ row }) => page * rowsPerPage + row.index + 1,
      },
      {
        header: 'Phone Number',
        accessorKey: 'Phone Number',
        Cell: ({ row }) => (
          <EllipsisWithTooltip
            text={row.original?.['Phone Number'] || row.original?.phone_number || '—'}
            fontWeight={500}
          />
        ),
      },
      {
        header: 'Company ID',
        accessorKey: 'Company ID',
        Cell: ({ row }) => (
          <EllipsisWithTooltip
            text={row.original?.['Company ID'] || row.original?.company_id || '—'}
          />
        ),
      },
      {
        header: 'Source',
        accessorKey: 'Source',
        Cell: ({ row }) => (
          <EllipsisWithTooltip text={row.original?.['Source'] || row.original?.source || '—'} />
        ),
      },
      {
        header: 'Status',
        accessorKey: 'Opted In',
        Cell: ({ row }) => (
          <OptedInChip value={row.original?.['Opted In'] ?? row.original?.is_optin} />
        ),
      },
      {
        header: 'Created At',
        accessorKey: 'Created At',
        Cell: ({ row }) => (
          <EllipsisWithTooltip
            text={formatDate(row.original?.['Created At'] || row.original?.created_at)}
            maxWidth={200}
          />
        ),
      },
      {
        header: 'Updated At',
        accessorKey: 'Updated At',
        Cell: ({ row }) => (
          <EllipsisWithTooltip
            text={formatDate(row.original?.['Updated At'] || row.original?.updated_at)}
            maxWidth={200}
          />
        ),
      },
      {
        header: 'Action',
        accessorKey: 'actions',
        size: 110,
        enableSorting: false,
        Cell: ({ row }) => {
          const phone = row.original?.['Phone Number'] || row.original?.phone_number || '';
          const isOptedIn = row.original?.['Opted In'] ?? row.original?.is_optin ?? true;
          if (!isOptedIn) {
            return (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Opted out
              </Typography>
            );
          }
          return (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={isOptingOut || !phone}
              onClick={() => {
                setOptoutTargetPhone(phone);
                setOptoutModalOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                py: 0.25,
                px: 1,
                minWidth: 'auto',
                lineHeight: 1.4,
              }}
            >
              Opt Out
            </Button>
          );
        },
      },
    ],
    [page, rowsPerPage, isOptingOut]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={styles.optinUsersToolbar}>
        <Box sx={styles.optinUsersToolbarInner}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
            >
              Opt-in subscribers
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25 }}
            >
              {isLoading
                ? 'Loading records…'
                : `${totalCount.toLocaleString('en-IN')} total record${totalCount === 1 ? '' : 's'}`}
            </Typography>
          </Box>

          <Box sx={styles.optinUsersToolbarActions}>
            <Tooltip title="Refresh list">
              <span>
                <IconButton
                  onClick={refresh}
                  aria-label="Refresh optin users"
                  size="small"
                  disabled={isLoading}
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.300',
                    bgcolor: 'background.paper',
                    width: 34,
                    height: 34,
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
              }
              onClick={downloadList}
              disabled={isDownloading || isLoading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '6px',
              }}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<PersonRemoveIcon fontSize="small" />}
              onClick={() => {
                setOptoutTargetPhone('');
                setOptoutModalOpen(true);
              }}
              disabled={isLoading || isOptingOut}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '6px',
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  bgcolor: 'rgba(211, 47, 47, 0.04)',
                },
              }}
            >
              Opt Out User
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={styles.optinUsersTableWrap}>
        <TemplateDataTable
          columns={columns}
          data={rows}
          state={{ isLoading }}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableHiding={false}
          enableStickyHeader
          enablePagination={false}
          muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 360px)' } }}
          renderBottomToolbar={() => (
            <Box sx={styles.optinUsersPaginationBar}>
              <Pagination
                totalCount={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Box>
          )}
        />
      </Box>

      <OptoutUserModal
        open={optoutModalOpen}
        onClose={() => {
          if (!isOptingOut) {
            setOptoutModalOpen(false);
            setOptoutTargetPhone('');
          }
        }}
        initialPhoneNumber={optoutTargetPhone}
        onConfirm={async (phone) => {
          await optoutUser(phone);
          setOptoutModalOpen(false);
          setOptoutTargetPhone('');
        }}
        isSubmitting={isOptingOut}
      />
    </Box>
  );
}
